import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { Instagram, ChevronLeft, ChevronRight, ImageOff } from "lucide-react";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IG_URL = "https://www.instagram.com/miguelfaria.photo/";

const IGTile = ({ url, idx }) => {
  const [errored, setErrored] = useState(false);
  return (
    <a
      href={IG_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="ig-tile shrink-0 snap-start"
      style={{ width: "calc((100% - (var(--ig-gap, 8px) * (var(--ig-cols, 3) - 1))) / var(--ig-cols, 3))" }}
      data-testid={`instagram-tile-${idx}`}
    >
      {errored ? (
        <div className="w-full h-full flex flex-col items-center justify-center bg-black text-white/40 gap-2 border border-[#c0392b]/40" data-testid={`instagram-tile-error-${idx}`}>
          <ImageOff size={28} />
          <span className="font-heading text-[10px] tracking-[0.25em] uppercase text-[#c0392b]">URL inválido</span>
        </div>
      ) : (
        <img
          src={url}
          alt={`Instagram ${idx + 1}`}
          loading="lazy"
          onError={() => setErrored(true)}
        />
      )}
    </a>
  );
};

const InstagramGrid = () => {
  const [items, setItems] = useState([]);
  const scrollerRef = useRef(null);
  const [hasLeft, setHasLeft] = useState(false);
  const [hasRight, setHasRight] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/instagram`)
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, []);

  const updateIndicators = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setHasLeft(el.scrollLeft > 4);
    setHasRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateIndicators();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateIndicators, { passive: true });
    window.addEventListener("resize", updateIndicators);
    return () => {
      el.removeEventListener("scroll", updateIndicators);
      window.removeEventListener("resize", updateIndicators);
    };
  }, [updateIndicators, items]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    // scroll one "page" worth (visible width)
    el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: "smooth" });
  };

  return (
    <section
      id="instagram"
      className="bg-black py-24 md:py-32 border-t border-white/10"
      data-testid="instagram-section"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-20">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="divider-red" />
                <span className="font-heading text-[#c0392b] tracking-[0.4em] text-xs uppercase">05 — Instagram</span>
              </div>
              <h2 className="font-heading text-white text-6xl md:text-8xl uppercase leading-[0.9]" data-testid="instagram-title">
                Diário<br />Visual
              </h2>
            </div>
            <p className="text-white/70 text-base md:text-lg max-w-md">
              Segue o trabalho em{" "}
              <a
                href={IG_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-[#c0392b] transition-colors underline underline-offset-4"
                data-testid="instagram-handle-link"
              >
                @miguelfaria.photo
              </a>
              .
            </p>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="relative">
            {/* Arrows */}
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              disabled={!hasLeft}
              className={`absolute top-1/2 -translate-y-1/2 left-2 md:-left-4 z-20 w-11 h-11 md:w-12 md:h-12 border bg-black flex items-center justify-center transition-all duration-200 ${
                hasLeft
                  ? "border-white text-white hover:border-[#c0392b] hover:text-[#c0392b] cursor-pointer"
                  : "border-white/15 text-white/20 cursor-not-allowed"
              }`}
              aria-label="Fotos anteriores"
              data-testid="instagram-arrow-left"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              disabled={!hasRight}
              className={`absolute top-1/2 -translate-y-1/2 right-2 md:-right-4 z-20 w-11 h-11 md:w-12 md:h-12 border bg-black flex items-center justify-center transition-all duration-200 ${
                hasRight
                  ? "border-white text-white hover:border-[#c0392b] hover:text-[#c0392b] cursor-pointer"
                  : "border-white/15 text-white/20 cursor-not-allowed"
              }`}
              aria-label="Fotos seguintes"
              data-testid="instagram-arrow-right"
            >
              <ChevronRight size={22} />
            </button>

            <div
              ref={scrollerRef}
              className="flex gap-2 overflow-x-auto snap-x snap-mandatory ig-cols"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              data-testid="instagram-grid"
            >
              <style>{`
                [data-testid="instagram-grid"]::-webkit-scrollbar{display:none}
                .ig-cols{--ig-cols:2; --ig-gap:8px}
                @media (min-width:768px){.ig-cols{--ig-cols:3}}
                @media (min-width:1024px){.ig-cols{--ig-cols:6}}
              `}</style>
              {items.map((it, idx) => (
                <IGTile key={it.id} url={it.url} idx={idx} />
              ))}
              {items.length === 0 && (
                <div className="w-full text-white/50 text-center py-16 font-heading tracking-[0.3em] uppercase text-sm">
                  Sem fotos do Instagram.
                </div>
              )}
            </div>
          </div>
        </Reveal>

        <Reveal delay={250}>
          <div className="mt-12 text-center">
            <a
              href={IG_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-primary inline-flex items-center gap-3"
              data-testid="instagram-cta"
            >
              <Instagram size={18} strokeWidth={2} />
              Seguir no Instagram
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default InstagramGrid;
